import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SignOut, WarningCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import IntegrationsCard from "@/components/IntegrationsCard";
import IntegrationsOutboxStatus from "@/components/IntegrationsOutboxStatus";

const OPTIONS = [
  { key: "email_notifications", label: "Email notifications", desc: "Receive an email when a project status changes." },
  { key: "product_updates",     label: "Product updates",     desc: "Occasional emails about new Agenaix capabilities." },
  { key: "weekly_digest",       label: "Weekly digest",       desc: "Monday morning summary of all your projects." },
];

export default function Settings() {
  const { signOut } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({data}) => setSettings(data)).catch(() => toast.error("Failed"));
  }, []);

  const toggle = async (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    setSaving(true);
    try { await api.patch("/settings", { [key]: next[key] }); }
    catch (e) { toast.error("Failed to save"); setSettings(settings); }
    finally { setSaving(false); }
  };

  return (
    <AppShell subtitle="§ PREFERENCES" title="Settings">
      <div className="max-w-3xl space-y-6">
        <div className="border border-black/10 p-8" data-testid="settings-notifications-card">
          <div className="fx-mono-tag mb-6">NOTIFICATIONS</div>
          <div className="space-y-5">
            {settings && OPTIONS.map(o => (
              <div key={o.key} className="flex items-start justify-between gap-6 pb-5 border-b border-black/5 last:border-b-0 last:pb-0">
                <div>
                  <Label className="text-sm font-semibold tracking-tight">{o.label}</Label>
                  <p className="text-xs text-neutral-500 mt-1">{o.desc}</p>
                </div>
                <Switch
                  checked={!!settings[o.key]}
                  onCheckedChange={() => toggle(o.key)}
                  disabled={saving}
                  data-testid={`settings-toggle-${o.key}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="border border-black/10 p-8" data-testid="settings-appearance-card">          <div className="fx-mono-tag mb-6">APPEARANCE</div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-semibold tracking-tight">Theme</Label>
              <p className="text-xs text-neutral-500 mt-1">Light mode is the current default. Dark theme is coming soon.</p>
            </div>
            <div className="fx-mono-tag px-3 py-1.5 border border-black/10 rounded-md">LIGHT</div>
          </div>
        </div>

        <IntegrationsCard />
        <IntegrationsOutboxStatus />

        <div className="border p-8" style={{borderColor:'#FF3B30', background:'#FFF6F5'}} data-testid="settings-danger-card">
          <div className="fx-mono-tag mb-4 flex items-center gap-2" style={{color:'#B72318'}}><WarningCircle size={14} weight="fill" /> DANGER ZONE</div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-semibold text-sm tracking-tight">Sign out of this device</div>
              <p className="text-xs text-neutral-600 mt-1">You'll need to log in again to access your workspace.</p>
            </div>
            <Button onClick={async () => { await signOut(); nav("/"); }} className="rounded-md h-10 bg-[#FF3B30] hover:bg-[#c72e26] text-white" data-testid="settings-signout-btn">
              <SignOut size={14} weight="bold" className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
